import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { WebhookContext } from "../services/context";
import { WebhookApi } from "../services/types";
import WebhookSettingsForm from "./WebhookSettingsForm";

interface Props {
	webhook: WebhookApi;
	onSuccess: (webhook: WebhookApi) => void;
}

const WebhookForm = (props: Props): JSX.Element => {
	const webhookService = React.useContext(WebhookContext);
	const form = useForm<WebhookApi>({
		criteriaMode: "all",
		defaultValues: {
			...props.webhook,
			settings: webhookService.includeSettings ? props.webhook.settings ?? [] : undefined,
		},
	});
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const {
		register,
		setError,
		handleSubmit,
		formState,
		control,
		formState: { errors },
		reset,
	} = form;
	const { addSuccessFlash, addFlash } = useFlash();

	const fetchResourceTypes = async (search: string, options?: Record<string, any>) => {
		const res = await webhookService.api().getResourceTypeOptions(search, options);
		return res
			.map((item) => ({
				value: item,
				label: t(`${webhookService._resourceTypeTranslationPrefix}.${item}`),
			}))
			.filter((item) => item.label.toLowerCase().includes(search?.toLowerCase()));
	};

	const getParsedDataForm = (data: WebhookApi) => {
		data.id = props.webhook.id;
		data.settings = (data.settings || []).filter((setting) => setting.name || setting.type || setting.value);

		return data;
	};

	const onSubmit = handleSubmit(async (data: WebhookApi) => {
		setLoading(true);
		const newData = getParsedDataForm(data);

		try {
			if (!newData.id) {
				const res = await webhookService.api().create(newData);
				addSuccessFlash(t("lib:common.flash.saved"));
				props.onSuccess(res.data.data);
			} else {
				const res = await webhookService.api().update(newData, { include: "settings" });
				addSuccessFlash(t("lib:common.flash.saved"));
				reset(res.data.data);
			}
		} catch (e) {
			handleError.form(e, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<FormDirty onSubmit={onSubmit} formState={formState} loading={loading}>
			<fieldset className={"form-group"}>
				<h5>{t("lib:common.word.basic_data")}</h5>
				<FormInput label={t("lib:common.word.name")} name="name" errors={errors} register={register} />
				<FormInput
					label={t("lib:go_component.webhook.form.url.label")}
					name="url"
					errors={errors}
					register={register}
				/>
				<FormSelectGroup
					label={t("lib:go_component.webhook.form.resource_type.label")}
					name="resource_type"
					errors={errors}
					loadOptions={fetchResourceTypes}
					defaultValue={
						props.webhook.resource_type && {
							label: t(`${webhookService._resourceTypeTranslationPrefix}.${props.webhook.resource_type}`),
							value: props.webhook.resource_type,
						}
					}
					control={control}
					data-testid="resource_type"
				/>
			</fieldset>
			{webhookService.includeSettings && <WebhookSettingsForm form={form} />}
		</FormDirty>
	);
};
export default WebhookForm;
